import { CheckCircle, Edit, Loader2, Trash2, X } from "lucide-react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import useToastWrapper from "../../../../../../hooks/useToastWrapper";
import { TabsContent } from "../../../../../../shared/components/tabs/Tabs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../shared/components/card/Card";
import { Button } from "../../../../../../shared/components/button/Button";
import { Label } from "../../../../../../shared/components/label/Label";
import { Input } from "../../../../../../shared/components/input/Input";
import { Switch } from "../../../../../../shared/components/switch/Switch";
import { Textarea } from "../../../../../../shared/components/textarea/Textarea";
import useAxios from "../../../../../../hooks/useAxios";
import { API_DATASETS_DETAILS } from "../../../../../../shared/lib/apiEndpointsResolver";

const DatasetTabContent = () => {
  const { id } = useParams();
  const axios = useAxios();
  const { t } = useTranslation();
  const { display, displayError, displayUnexpectedError } = useToastWrapper();
  const [dataset, setDataset] = useState({
    code: "",
    name: { es: "", en: "" },
    description: { es: "", en: "" },
    active: true,
    etlQuery: "",
  });
  const [isDatasetDialogOpen, setIsDatasetDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [datasetToDelete, setDatasetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);

  const openDatasetDialog = () => {
    setIsDatasetDialogOpen(true);
  };

  const onAddDatasetSuccessfully = (newDataset) => {
    // TODO: debe recargar la página con el nuevo dataset
    // TODO: debe mostrar un toast de exito
  };

  const validateDatasetForm = (form) => {
    const errors = {};

    if (!form.code.trim()) {
      errors.code = t("datasetsCentre.common.codeRequired");
    }

    if (!form.name.en.trim()) {
      errors.nameEn = t("datasetsCentre.common.nameEnRequired");
    }

    if (!form.name.es.trim()) {
      errors.nameEs = t("datasetsCentre.common.nameEsRequired");
    }

    if (!form.description.en.trim()) {
      errors.descriptionEn = t("datasetsCentre.common.descriptionEnRequired");
    }

    if (!form.description.es.trim()) {
      errors.descriptionEs = t("datasetsCentre.common.descriptionEsRequired");
    }

    if (!form.etlQuery.trim()) {
      errors.descriptionEs = t("datasetsCentre.datasets.etlQueryRequired");
    }

    return errors;
  };

  const startEditingDataset = (dataset) => {
    setEditingId(id);
    setEditingData({ ...dataset });
  };

  const cancelEditingDataset = () => {
    setEditingId(null);
    setEditingData(null);
    setFormErrors({});
  };

  const saveEditingDataset = async () => {
    if (!editingData) return;

    const errors = validateDatasetForm(editingData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.put(
        API_DATASETS_DETAILS(editingId),
        editingData,
      );

      if (result.status === 200) {
        const updatedDataset = result.data;
        setEditingId(null);
        setEditingData(null);
        setFormErrors({});
        setDataset(updatedDataset);
        display(
          t("datasetsCentre.common.success"),
          t("datasetsCentre.datasets.updateSuccess", {
            code: updatedDataset.code,
          }),
        );
      } else {
        console.error(result.data);
        setUnexpectedError(true);
      }
    } catch (error) {
      console.error(error);
      setUnexpectedError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayDeleteDatasetDialog = (dataset) => {
    setDatasetToDelete(dataset);
  };

  const cancelDeleteDataset = () => {
    setDatasetToDelete(null);
  };

  const deleteDataset = async (id) => {
    // TODO: por hacer
  };

  const isEditing = editingId !== null;
  const displayData = isEditing ? editingData : dataset;

  useEffect(() => {
    const loadDataset = async () => {
      try {
        const response = await axios.get(API_DATASETS_DETAILS(id));
        const data = response.data;
        setDataset(data);
      } catch (error) {
        const data = error?.response?.data;
        console.error(data);
        displayUnexpectedError();
      }
    };

    loadDataset();
  }, [id]);

  return (
    <>
      <TabsContent value="overview" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Datasets</h2>
            <p className="text-muted-foreground">
              Manage dataset configurations and SQL queries
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Card
            key={dataset.id}
            className={isEditing ? "ring-2 ring-primary" : ""}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {displayData.code || `Dataset #1`}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={saveEditingDataset}
                        disabled={isSubmitting}
                        className="text-green-600 hover:text-green-700"
                      >
                        {isSubmitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelEditingDataset}
                        disabled={isSubmitting}
                        className="text-gray-600 hover:text-gray-700"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEditingDataset(dataset)}
                        disabled={editingId !== null}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        //onClick={() => confirmDeleteDataset(dataset.id)}
                        disabled={editingId !== null}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dataset Code</Label>
                  {isEditing ? (
                    <>
                      <Input
                        value={displayData.code}
                        onChange={(e) =>
                          setEditingData((prev) =>
                            prev ? { ...prev, code: e.target.value } : null,
                          )
                        }
                        placeholder="e.g., sales_data"
                        className={formErrors.code ? "border-destructive" : ""}
                      />
                      {formErrors.code && (
                        <p className="text-sm text-destructive">
                          {formErrors.code}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 bg-muted rounded-md text-sm">
                      {displayData.code}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Pipeline Status</Label>
                  {isEditing ? (
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={displayData.enabled}
                        onCheckedChange={(checked) =>
                          setEditingData((prev) =>
                            prev ? { ...prev, enabled: checked } : null,
                          )
                        }
                      />
                      <Label className="text-sm font-normal">
                        {displayData.enabled
                          ? "Enabled in pipeline"
                          : "Disabled from pipeline"}
                      </Label>
                    </div>
                  ) : (
                    <div className="px-3 py-2 bg-muted rounded-md text-sm">
                      {displayData.enabled
                        ? "Enabled in pipeline"
                        : "Disabled from pipeline"}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name (English)</Label>
                  {isEditing ? (
                    <>
                      <Input
                        value={displayData.name.en}
                        onChange={(e) =>
                          setEditingData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  name: {
                                    ...prev.name,
                                    en: e.target.value,
                                  },
                                }
                              : null,
                          )
                        }
                        placeholder="Dataset name in English"
                        className={
                          formErrors.nameEn ? "border-destructive" : ""
                        }
                      />
                      {formErrors.nameEn && (
                        <p className="text-sm text-destructive">
                          {formErrors.nameEn}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 bg-muted rounded-md text-sm">
                      {displayData.name.en}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Name (Spanish)</Label>
                  {isEditing ? (
                    <>
                      <Input
                        value={displayData.name.es}
                        onChange={(e) =>
                          setEditingData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  name: {
                                    ...prev.name,
                                    es: e.target.value,
                                  },
                                }
                              : null,
                          )
                        }
                        placeholder="Dataset name in Spanish"
                        className={
                          formErrors.nameEs ? "border-destructive" : ""
                        }
                      />
                      {formErrors.nameEs && (
                        <p className="text-sm text-destructive">
                          {formErrors.nameEs}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 bg-muted rounded-md text-sm">
                      {displayData.name.es}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Description (English)</Label>
                  {isEditing ? (
                    <Textarea
                      value={displayData.description.en}
                      onChange={(e) =>
                        setEditingData((prev) =>
                          prev
                            ? {
                                ...prev,
                                description: {
                                  ...prev.description,
                                  en: e.target.value,
                                },
                              }
                            : null,
                        )
                      }
                      placeholder="Dataset description in English"
                      rows={3}
                    />
                  ) : (
                    <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[80px]">
                      {displayData.description.en}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Description (Spanish)</Label>
                  {isEditing ? (
                    <Textarea
                      value={displayData.description.es}
                      onChange={(e) =>
                        setEditingData((prev) =>
                          prev
                            ? {
                                ...prev,
                                description: {
                                  ...prev.description,
                                  es: e.target.value,
                                },
                              }
                            : null,
                        )
                      }
                      placeholder="Dataset description in Spanish"
                      rows={3}
                    />
                  ) : (
                    <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[80px]">
                      {displayData.description.es}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label>SQL Query</Label>
                {isEditing ? (
                  <>
                    <Textarea
                      value={displayData.etlQuery}
                      onChange={(e) =>
                        setEditingData((prev) =>
                          prev ? { ...prev, etlQuery: e.target.value } : null,
                        )
                      }
                      placeholder="SELECT * FROM table_name"
                      rows={4}
                      className={`font-mono text-sm ${formErrors.etlQuery ? "border-destructive" : ""}`}
                    />
                    {formErrors.etlQuery && (
                      <p className="text-sm text-destructive">
                        {formErrors.etlQuery}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono min-h-[100px] whitespace-pre-wrap">
                    {displayData.etlQuery}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>
    </>
  );
};

export default DatasetTabContent;
